/**
 * Storefront filtering controller for sections/collection-native-filters.liquid: progressive
 * enhancement that turns the plain GET <form> filter panel into an AJAX experience (no full
 * reload), fetching templates/collection.native-filters-ajax.liquid (?view=native-filters-ajax).
 * Without this file the panel still filters via normal form submission.
 *
 * Reuses the theme's own drawer/popover/scroller/layout-switch/loading-bar (CollectionSection in
 * theme.js, still boots via data-section-type="collection"). Its tag based handlers
 * (toggle-tag/apply-tags/reset-tags/select-value) must never fire here — their URL builder would
 * drop every filter.p.* parameter.
 */
(() => {
  const FORM_SELECTOR = '[data-collection-filters-form]'
  const PRODUCTS_SELECTOR = '.CollectionInner__Products'
  const DESKTOP_QUERY = 'screen and (min-width: 1008px)' // theme.js Responsive helper: 'lap-and-up'

  const forms = Array.from(document.querySelectorAll(FORM_SELECTOR))
  const productsElement = document.querySelector(PRODUCTS_SELECTOR)

  if (!forms.length || !productsElement) return

  const drawer = document.getElementById('collection-filter-drawer')
  const sortPopover = document.getElementById('collection-sort-popover')
  const toolbarFilterButton = document.querySelector('.CollectionToolbar__Item--filter')
  const mainElement = document.querySelector('.CollectionMain')

  let request = null

  // theme.js reads event.state.path on popstate without a null check. Giving every entry a state
  // object (and never setting .path) keeps that handler quiet; the reload below does the work.
  window.history.replaceState({}, '', window.location.href)

  /**
   * Filters currently applied, as they are in the URL. Kept as the single source of truth so the two
   * mirrored forms (drawer and sidebar) can always be restored to the applied state.
   */
  function appliedParams() {
    return new URLSearchParams(window.location.search)
  }

  /** Every applied filter value, whatever facet it belongs to. */
  function filterValues(params) {
    const values = []
    params.forEach((value, key) => {
      if (key.indexOf('filter.') === 0) values.push(value)
    })
    return values
  }

  /** Copies the applied filters onto another URLSearchParams, keeping their parameter names. */
  function copyFilters(from, to) {
    from.forEach((value, key) => {
      if (key.indexOf('filter.') === 0) to.append(key, value)
    })
  }

  /** Serialises a form, dropping empty values and the pagination cursor. */
  function paramsFromForm(form) {
    const params = new URLSearchParams()

    new FormData(form).forEach((value, key) => {
      if (value !== '') params.append(key, value)
    })

    return params
  }

  /**
   * Reflects a checkbox state on its row. The tick the customer sees is the ::before image of
   * .Linklist__Item.is-selected, not the input itself, so this has to run on every change and not
   * only when the filters are applied — otherwise ticking a box in the drawer looks like nothing
   * happened until "Apply".
   */
  function syncRow(input) {
    const item = input.closest('.Linklist__Item')
    if (item) item.classList.toggle('is-selected', input.checked)

    const label = input.nextElementSibling
    if (label) label.classList.toggle('is-active', input.checked)
  }

  /** Mirrors the applied state onto every form, so drawer and sidebar never disagree. */
  function syncForms(params) {
    const active = filterValues(params)
    const sortBy = params.get('sort_by') || ''

    forms.forEach((form) => {
      form.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach((input) => {
        input.checked = active.includes(input.value)
        syncRow(input)
      })

      const sortInput = form.querySelector('input[name="sort_by"]')
      if (sortInput && sortBy) sortInput.value = sortBy
    })
  }

  /** Same count badge the theme writes in _reloadProducts, so the toolbar stays consistent. */
  function syncToolbar(params) {
    const count = filterValues(params).length

    document.querySelectorAll('[data-collection-filters-reset]').forEach((reset) => {
      reset.style.display = count === 0 ? 'none' : ''
      reset.setAttribute('href', resetUrl(params))
    })

    if (!toolbarFilterButton) return

    const badge = toolbarFilterButton.querySelector('span')
    if (badge) toolbarFilterButton.removeChild(badge)

    if (count === 0) {
      toolbarFilterButton.classList.add('Text--subdued')
    } else {
      toolbarFilterButton.classList.remove('Text--subdued')
      toolbarFilterButton.innerHTML += `<span class="Text--subdued">(${count})</span>`
    }
  }

  function resetUrl(params) {
    const sortBy = params.get('sort_by')
    return window.location.pathname + (sortBy ? `?sort_by=${encodeURIComponent(sortBy)}` : '')
  }

  /** Keeps the sort links pointing at the filters that are currently applied. */
  function syncSortLinks(params) {
    if (!sortPopover) return

    sortPopover.querySelectorAll('[data-collection-sort-value]').forEach((link) => {
      const value = link.getAttribute('data-collection-sort-value')
      const linkParams = new URLSearchParams()

      copyFilters(params, linkParams)
      linkParams.set('sort_by', value)

      link.setAttribute('href', `${window.location.pathname}?${linkParams.toString()}`)
      link.classList.toggle('is-selected', value === params.get('sort_by'))
    })
  }

  /**
   * Closes the drawer / popover through the theme's own components: their close buttons are bound by
   * Drawer and Popover, and those instances are not reachable from here.
   */
  function closeOverlay(container) {
    if (!container || container.getAttribute('aria-hidden') !== 'false') return

    const closeButton = container.querySelector('[data-action="close-drawer"], [data-action="close-popover"]')

    if (closeButton) closeButton.click()
  }

  function apply(params) {
    const search = params.toString()
    const url = window.location.pathname + (search ? `?${search}` : '')

    document.dispatchEvent(new CustomEvent('theme:loading:start'))

    window.history.pushState({}, '', url)

    syncForms(params)
    syncToolbar(params)
    syncSortLinks(params)

    // Cancel the previous request: clicking two brands quickly must not race back a stale grid.
    if (request) request.abort()
    request = new AbortController()

    const viewParams = new URLSearchParams(params)
    viewParams.set('view', 'native-filters-ajax')

    fetch(`${window.location.pathname}?${viewParams.toString()}`, {
      credentials: 'same-origin',
      signal: request.signal
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Filtering failed with status ${response.status}`)
        return response.text()
      })
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html')
        const section = parsed.querySelector('.shopify-section')

        if (!section) throw new Error('Filtering response has no section to render')

        productsElement.innerHTML = section.innerHTML

        // Marks the fresh items as not covered by the theme's reveal animation, see the section CSS.
        productsElement.classList.add('is-ajax-rendered')

        // The grid container was replaced, so its progressive reveal has to be wired again.
        if (typeof window.pdmCollectionEndlessScroll === 'function') {
          window.pdmCollectionEndlessScroll()
        }

        document.dispatchEvent(new CustomEvent('theme:loading:end'))

        if (mainElement && mainElement.getBoundingClientRect().top < 0) {
          window.scrollTo({ top: mainElement.offsetTop, behavior: 'smooth' })
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') return

        // Never leave the loading bar hanging, and fall back to a normal navigation.
        document.dispatchEvent(new CustomEvent('theme:loading:end'))
        console.error(error)
        window.location.href = url
      })
  }

  function applyFromForm(form) {
    apply(paramsFromForm(form))
  }

  forms.forEach((form) => {
    // The no-JavaScript submit button is redundant once this controller runs.
    const submitButton = form.querySelector('[data-collection-filters-submit]')
    if (submitButton) submitButton.style.display = 'none'

    form.addEventListener('submit', (event) => {
      event.preventDefault()
      applyFromForm(form)
    })

    form.addEventListener('change', (event) => {
      if (!event.target.matches('input[type="checkbox"][name^="filter."]')) return

      syncRow(event.target)

      // Same rule as the theme: the always visible sidebar applies on the spot, the drawer waits for
      // "Apply". matchMedia is read on every change so it follows a resize.
      if (window.matchMedia(DESKTOP_QUERY).matches && form.dataset.filtersScope === 'sidebar') {
        applyFromForm(form)
      }
    })
  })

  document.addEventListener('click', (event) => {
    const reset = event.target.closest('[data-collection-filters-reset]')

    if (reset) {
      event.preventDefault()

      const params = new URLSearchParams()
      const sortBy = appliedParams().get('sort_by')
      if (sortBy) params.set('sort_by', sortBy)

      apply(params)
      closeOverlay(drawer)
      return
    }

    const sortLink = event.target.closest('[data-collection-sort-value]')

    if (sortLink) {
      event.preventDefault()

      const params = appliedParams()
      params.set('sort_by', sortLink.getAttribute('data-collection-sort-value'))
      params.delete('page')

      apply(params)
      closeOverlay(sortPopover)
    }
  })

  // Opening the drawer must always show what is actually applied, in case it was closed without
  // applying the boxes that had been ticked.
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-drawer-id="collection-filter-drawer"][data-action="open-drawer"]')) {
      syncForms(appliedParams())
    }
  })

  window.addEventListener('popstate', () => {
    window.location.reload()
  })
})()
